import React from "react";
import { Card, Typography, Space, Row, Col, Statistic } from "antd";
import {
  HomeOutlined,
  DollarOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import CustomCard from "../../blocks/Card/Card";

const { Text, Title } = Typography;

// Sample data for nearby properties
const nearbyProperties = [
  { distance: 0.1, value: 2300000, sqft: 2500 },
  { distance: 0.2, value: 3500000, sqft: 3000 },
  { distance: 0.3, value: 4200000, sqft: 3500 },
  { distance: 0.4, value: 5100000, sqft: 4000 },
  { distance: 0.5, value: 6700000, sqft: 5000 },
  { distance: 0.6, value: 4500000, sqft: 3200 },
  { distance: 0.7, value: 3900000, sqft: 2800 },
  { distance: 0.8, value: 2800000, sqft: 2200 },
  { distance: 0.9, value: 3200000, sqft: 2700 },
  { distance: 1.0, value: 3700000, sqft: 3200 },
  // Add more data points as needed
];

const subjectProperty = { distance: 0, value: 4000000, sqft: 3872 };

const MicroMarketAnalysis = () => {
  const averageValue =
    nearbyProperties.reduce((sum, prop) => sum + prop.value, 0) /
    nearbyProperties.length;
  const potentialIncrease = Math.max(0, averageValue - subjectProperty.value);

  return (
    <CustomCard
      title={
        <Space>
          <HomeOutlined />
          <span>Micro-Market Analysis (0.5 mile radius)</span>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Lowest Nearby Value"
              value={2300000}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Highest Nearby Value"
              value={6700000}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Potential Value Increase"
              value={potentialIncrease}
              prefix={<ArrowUpOutlined />}
              valueStyle={{ color: "#3f8600" }}
              formatter={(value) => `$${(value / 1000000).toFixed(2)}M`}
            />
          </Col>
        </Row>
        <Card>
          <Title level={4}>Property Values vs. Distance</Title>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <XAxis
                type="number"
                dataKey="distance"
                name="Distance"
                unit=" miles"
              />
              <YAxis
                type="number"
                dataKey="value"
                name="Value"
                unit="M"
                tickFormatter={(value) => `$${value / 1000000}M`}
              />
              <ZAxis
                type="number"
                dataKey="sqft"
                range={[60, 400]}
                name="Square Feet"
                unit=" sqft"
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                formatter={(value, name, props) => {
                  if (name === "Value")
                    return [`$${(value / 1000000).toFixed(2)}M`, name];
                  if (name === "Square Feet") return [`${value} sqft`, name];
                  return [value, name];
                }}
              />
              <Scatter
                name="Properties"
                data={nearbyProperties}
                fill="#8884d8"
              />
              <Scatter
                name="Subject Property"
                data={[subjectProperty]}
                fill="#ff7300"
              />
            </ScatterChart>
          </ResponsiveContainer>
          <Text type="secondary">
            This chart shows property values in relation to their distance from
            the subject property. The size of each point represents the square
            footage of the property.
          </Text>
        </Card>
        <Text>
          The immediate neighborhood shows a wide range of property values, from
          $2.3 million to $6.7 million. This diversity suggests potential for
          value appreciation if our subject property is currently valued on the
          lower end of local comparables. Factors such as specific location,
          condition, and unique features contribute to this value spread.
        </Text>
        <Text type="secondary">
          Note: This analysis is based on a small sample of nearby properties.
          For a more comprehensive understanding, consider consulting with a
          local real estate expert who can provide insights into specific
          property characteristics and micro-market trends.
        </Text>
      </Space>
    </CustomCard>
  );
};

export default MicroMarketAnalysis;
